import React from 'react';

interface ProfessionEntry {
  id: string;
  name: string;
  category: string; // 'job' | 'specialization'
  xp: number;
  rankName: string;
  xpRequired: number | null; // null = already at this slot's max rank
}

export interface CharacterState {
  name: string;
  isFemale: boolean | null;
  professions: ProfessionEntry[];
  faction: { name: string; rankName: string } | null;
  // Every membership (a citizen of a hold can also be in a group).
  factions?: Array<{ id: string; name: string; rankName: string }>;
  septims: number;
  houses: number;
  sessionMinutes: number;
}

interface Props {
  character: CharacterState | null;
  portraitImage: string | null;
}

function formatSession(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours} h ${String(mins).padStart(2, '0')}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join('');
}

const CharacterPage: React.FC<Props> = ({ character, portraitImage }) => {
  if (!character) {
    return <p className="esc-empty">Chargement...</p>;
  }

  const genderLabel = character.isFemale === null ? '' : character.isFemale ? 'Femme' : 'Homme';
  const factions = character.factions && character.factions.length > 0
    ? character.factions
    : character.faction
      ? [{ id: 'primary', ...character.faction }]
      : [];
  const name = character.name || 'Personnage';

  return (
    <div className="esc-character">
      <section className="esc-card esc-identitycard">
        <div
          className={'esc-avatar' + (portraitImage ? ' esc-avatar--photo' : '')}
          style={portraitImage ? { backgroundImage: `url(${portraitImage})` } : undefined}
          aria-hidden="true"
        >
          {!portraitImage && initials(name)}
        </div>
        <div className="esc-identitycard__text">
          <h2 className="esc-identitycard__name">{name}</h2>
          {genderLabel && <p className="esc-identitycard__meta">{genderLabel}</p>}
          {factions.length > 0 && (
            <ul className="esc-chips" aria-label="Appartenances">
              {factions.map((f) => (
                <li className="esc-chip" key={f.id}>
                  <span className="esc-chip__name">{f.name}</span>
                  <span className="esc-chip__rank">{f.rankName}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="esc-stats" aria-label="Résumé">
        <div className="esc-stat">
          <span className="esc-stat__value">{character.septims.toLocaleString('fr-FR')}</span>
          <span className="esc-stat__label">Septims</span>
        </div>
        <div className="esc-stat">
          <span className="esc-stat__value">{character.houses}</span>
          <span className="esc-stat__label">{character.houses > 1 ? 'Maisons' : 'Maison'}</span>
        </div>
        <div className="esc-stat">
          <span className="esc-stat__value">{formatSession(character.sessionMinutes)}</span>
          <span className="esc-stat__label">Session en cours</span>
        </div>
      </section>

      <section className="esc-card esc-character__jobs">
        <h3 className="esc-card__title">Métiers</h3>
        {character.professions.length === 0 ? (
          <p className="esc-empty">Aucun métier choisi. Ouvre le menu des métiers avec la touche K.</p>
        ) : (
          <ul className="esc-jobs">
            {character.professions.map((p) => {
              const maxed = p.xpRequired === null;
              const pct = maxed ? 100 : Math.min(100, Math.round((p.xp / (p.xpRequired as number)) * 100));
              return (
                <li className="esc-job" key={p.id}>
                  <div className="esc-job__head">
                    <span className="esc-job__name">{p.name}</span>
                    <span className="esc-job__rank">{p.rankName}</span>
                  </div>
                  <div
                    className="esc-meter"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                    aria-label={`Expérience ${p.name}`}
                  >
                    <div className="esc-meter__fill" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="esc-job__xp">{maxed ? 'Rang maximum' : `${p.xp} sur ${p.xpRequired} XP`}</span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

export default CharacterPage;
